import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { getUser, hasCapability } from 'helpers/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components';
import moment from 'moment';
import { assets } from 'helpers/generic';
import ProbesWarningGlobe from "components/ProbesWarningsGlobe";
import SuppliesOrdersTcReviewWarningGlobe from "components/SuppliesOrdersTcReviewWarningGlobe";

const Navbar = styled.nav`
	background: white;
	padding: 0;
	border-bottom: 1px solid lightgray;

	.navbar-brand {
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;

		@media (max-width: 768px) {
			padding: 10px 10px;
		}

		@media (min-width: 768px) {
			width: 230px;
			align-items: center;

			img {
				width: 140px;
			}
		}
	}

	.navbar-toggler {
		outline: none !important;
		box-shadow: none !important;
	}

	.navbar-nav {
		width: 100%;
	}	

	.navbar-collapse {
		border-left: 1px solid lightgray;
		padding: 0;
	}

	#user {
		display: flex;
		flex-direction: column;
		justify-content: center;
		font-size: 12px;
		line-height: 12px;
		margin-top: 5px;

		small {
			color: gray;
		}

		span {
			color: #333;
			text-transform: uppercase;
			margin-bottom: 2px;
			border-bottom: 1px solid gray;
		}

		a {
			font-size: 10px;
			color: var(--plenoil-blue-light);
			text-decoration: none;
		}

		@media (max-width: 992px) {
			display: block !important;
			text-align: center;
			border-bottom: 1px solid lightgray;

			small {
				display: block;
			}

			a {
				display: block;
				margin: 0 auto !important;
				margin-top: 5px !important;
				margin-bottom: 0 !important;
			}
		}
	}		

	#clock {
		position: relative;
		display: flex;
		height: 100%;
		justify-content: center;
		align-items: center;
		padding-right: 10px;
		border-right: 1px solid lightgray;
		color: gray;

		i {
			font-size: 12px;
			margin-right: 5px;
		}

		span {
			font-size: 30px;
			font-weight: lighter;
		}

		@media (max-width: 992px) {
			border-right: 0;
			border-bottom: 1px solid lightgray;
		}
	}
`;

const Content = styled.section`
	max-width: 100vw;
	margin-top: 70px;
	padding: 0 10px;
	box-sizing: border-box;

	@media (min-width: 851px) and (max-width: 1439px) {
		& {
			padding: 0 30px;
			width: 100vw;
			margin-left: 0px;
		}
	}

	margin-left: ${props => props.sidebarVisible ? '230px' : '0'};

	transition: margin-left 0.3s ease;

	/* Desktop grande */
	@media (min-width: 1440px) {
		margin-left: 245px;
		width: calc(100vw - ${props => props.sidebarWidth}px);
	}

	/* Mobile */
	@media (max-width: 850px) {
		margin-left: 0;
	}
`;

const Sidebar = styled.div`
	--top: ${props => props.top}px;

	position: fixed;
	top: var(--top);

	left: ${props => props.visible ? '0' : '-245px'};
	z-index: 20;

	overflow-y: auto;
	overflow: visible;
	min-width: 245px;
	height: calc(100% - var(--top));

	padding-top: 20px;
	padding-right: 5px;
	background: white;

	transition: left 0.3s ease;

	/* Desktop grande: siempre visible */
	@media (min-width: 1440px) {
		left: 0;
	}

	/* Solo tablets */
	@media (min-width: 851px) and (max-width: 1439px) {
		background-color: #fff !important;
		box-shadow: var(--bs-box-shadow) !important;
	}

	/* Mobile: usa navbarCollapse */
	@media (max-width: 850px) {
		left: ${props => props.opened ? 0 : '-100%'};
	}
`;

const SidebarToggleButton = styled.button`
	position: absolute;
	top: 15px;
	left: calc(100% - 15px);
	z-index: 30;

	background: white;
	border: 1px solid var(--plenoil-blue);
	color: var(--plenoil-blue);
	font-size: 18px;
	line-height: 100%;

	border-radius: 100%;

	width: 30px;
	height: 30px;

	cursor: pointer;

	transition: left 0.3s ease;

	display: none;

	/* Solo tablets */
	@media (min-width: 851px) and (max-width: 1439px) {
		display: block;
	}
`;

const SidebarTitle = styled.div`
	display: flex;
	align-items: center;
	font-size: 25px;
	color: var(--plenoil-gray);
	border-bottom: 1px solid transparent;
	border-bottom-color: ${props => props.selected ? 'var(--plenoil-blue)' : 'var(--plenoil-orange)'};
	border-bottom-width: ${props => props.selected ? 8 : 2}px;
	padding-left: 40px;
	text-transform: uppercase;
	margin-bottom: 15px;

	a {
		color: unset;
		text-decoration: none;
	}

	&:not(:first-of-type) {
		margin-top: 50px;
	}

	i {
		color: ${props => props.selected ? 'var(--plenoil-blue)' : 'var(--plenoil-orange)'};
		transition: color 0.2s;
	}
`;

const SidebarItem = styled.div`
	font-weight: 500;
	text-transform: uppercase;
	margin-left: 45px;
	font-size: 14px;

	a {
		color: unset;
		text-decoration: none;

		&.active {
			color: var(--plenoil-blue);
			font-weight: bold;
		}
	}
`;


let lastSidebarTop = 0;
let timeInterval = 0;

export default function AppLayout(props) {
	let location = useLocation();

	const navbarRef = useRef(null);

	const [navbarCollapse, setNavbarCollapse] = useState(true);
	const [sidebarTop, setSidebarTop] = useState(lastSidebarTop);
	const [sidebarVisible, setSidebarVisible] = useState(true);

	const sidebarRef = useRef(null);
	const [sidebarWidth, setSidebarWidth] = useState(245);

	// Reposition sidebar
	useEffect(() => {
		let newSidebarTop = navbarRef.current.getBoundingClientRect().height;
		if ( lastSidebarTop === newSidebarTop ) return;
		lastSidebarTop = newSidebarTop;
		setSidebarTop(newSidebarTop);
	}, [navbarCollapse]);

	useEffect(() => {
		const updateSidebarWidth = () => {
			if (sidebarRef.current) {
				setSidebarWidth(sidebarRef.current.offsetWidth + 10);
			}
		};
		updateSidebarWidth();
		window.addEventListener('resize', updateSidebarWidth);
		return () => {
			window.removeEventListener('resize', updateSidebarWidth);
		};
	}, [sidebarVisible, navbarCollapse]);

	return (
		<React.Fragment>
			<Navbar ref={navbarRef} className="navbar fixed-top navbar-expand-lg">
	        	<NavLink to="/" className="navbar-brand">
	        		<img src={assets("/img/logo-plenergy.svg")} alt="plenergy" />
	        	</NavLink>
				
				<button onClick={() => setNavbarCollapse(!navbarCollapse)} className="navbar-toggler" type="button">
					<FontAwesomeIcon icon={faBars} />
			    </button>

			    <div className={(navbarCollapse ? 'collapse' : '') + ' navbar-collapse'}>
			      	<div id="user" className="d-flex flex-column me-auto justify-content-center py-2 py-lg-0 ps-2">
						<small>BIENVENIDO</small>
						<span>{ getUser()?.name}</span>
	        			<NavLink to="/logout" className="btn btn-link p-0 m-0">DESCONECTAR</NavLink>
					</div>

					<Clock />

					<div className="d-flex ms-2 justify-content-center py-2 py-lg-0 me-2">
						&nbsp;&nbsp;&nbsp;&nbsp;
						&nbsp;&nbsp;&nbsp;&nbsp;
						&nbsp;&nbsp;&nbsp;&nbsp;
						&nbsp;&nbsp;&nbsp;&nbsp;
					</div>
			    </div>
			</Navbar>


			<Sidebar
				opened={!navbarCollapse}
				visible={sidebarVisible}
				top={sidebarTop}
				ref={sidebarRef}
			>
				<SidebarToggleButton
					visible={sidebarVisible}
					onClick={() => setSidebarVisible(!sidebarVisible)}
				>
					{ sidebarVisible ? <i class="bi bi-arrow-left"></i> : <i class="bi bi-arrow-right"></i> }
				</SidebarToggleButton>
				{ hasCapability('tankertrucks') &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('tankertrucks') !== -1}>
							<NavLink to="/tankertrucks"><i className="bi bi-truck"></i> Cisternas</NavLink>
						</SidebarTitle>

						{ location.pathname.indexOf('tankertrucks') !== -1 && 
							<>
								<SidebarItem>
									<NavLink to="/tankertrucks/"><i className="bi bi-chevron-right"></i> Listado cisternas</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/tankertrucks/add-manual"><i className="bi bi-chevron-right"></i> Envío manual</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/tankertrucks/status/0"><i className="bi bi-chevron-right"></i> Programadas</NavLink>	
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/tankertrucks/status/1"><i className="bi bi-chevron-right"></i> En camino</NavLink>	
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/tankertrucks/status/2"><i className="bi bi-chevron-right"></i> Con retraso</NavLink>	
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/tankertrucks/status/3"><i className="bi bi-chevron-right"></i> Incidencias</NavLink>	
								</SidebarItem>
							</>
						}
					</>
				}


				{ hasCapability('prices') &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('prices') !== -1}>
							<NavLink to="/prices"><i className="bi bi-droplet-half"></i> Precios</NavLink>
						</SidebarTitle>

						{ location.pathname.indexOf('prices') !== -1 && 
							<>
								<SidebarItem>
									<NavLink to="/prices"><i className="bi bi-chevron-right"></i> Competencia</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/prices-check"><i className="bi bi-chevron-right"></i> Comprobación</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/prices-zones"><i className="bi bi-chevron-right"></i> Zonas</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/prices-app"><i className="bi bi-chevron-right"></i> Precios App</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/prices-indicus-webservice-sends"><i className="bi bi-chevron-right"></i> Envíos WS Indicus</NavLink>
								</SidebarItem>
							</>
						}
					</>
				}

				{ hasCapability('probes') &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('probes') !== -1}>
							<NavLink to="/probes" className="me-3"><i className="bi bi-card-list"></i> Sondas</NavLink>
							<ProbesWarningGlobe />
						</SidebarTitle>
						
						{ location.pathname.indexOf('probes') !== -1 && 
							<>
								<SidebarItem>
									<NavLink to="/probes"><i className="bi bi-chevron-right"></i> Listado sondas</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/zones"><i className="bi bi-chevron-right"></i> Zonas</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/middleware-stations"><i className="bi bi-chevron-right"></i> Estaciones</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/probes-warnings"><i className="bi bi-chevron-right"></i> Avisos</NavLink>
								</SidebarItem>
							</>
						}
					</>
				}

				{ hasCapability('supplies') &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('supplies') !== -1}>
							<NavLink to="/supplies/station" className="d-flex flex-row flex-nowrap align-items-center gap-2"><i className="bi bi-printer"></i> Suministros</NavLink>			
						</SidebarTitle>

						{ location.pathname.indexOf('supplies') !== -1 && 
							<>
								<SidebarItem>
									<NavLink to="/supplies/station"><i className="bi bi-chevron-right"></i> Inventario</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/supplies/bc-orderstc/station"><i className="bi bi-chevron-right"></i> Comprobaciones</NavLink>
									
									<div className="d-inline-flex ms-2">
										<SuppliesOrdersTcReviewWarningGlobe />
									</div>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/supplies/planning/station"><i className="bi bi-chevron-right"></i> Planificación</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/supplies/wash-switch"><i className="bi bi-chevron-right"></i> Activación lavaderos</NavLink>
								</SidebarItem>
							</>
						}			
					</>
				}

				{ hasCapability('conteos') &&
					<SidebarTitle selected={location.pathname.indexOf('conteos') !== -1}>
						<NavLink to="/conteos"><i className="bi bi-arrow-counterclockwise"></i> Conteos</NavLink>						
					</SidebarTitle>
				}

				{ (hasCapability('clockings_primary') || hasCapability('clockings_secondary')) &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('clockings') !== -1}>
							<NavLink to={'/clockings/' + (hasCapability('clockings_primary') ? 'responsibles' : 'dispensers-schedules')}><i className="bi bi-file-spreadsheet"></i> Fichajes</NavLink>			
						</SidebarTitle>

						{ location.pathname.indexOf('clockings') !== -1 && 
							<>
								{ hasCapability('clockings_primary') &&
									<SidebarItem>
										<NavLink to="/clockings/responsibles"><i className="bi bi-chevron-right"></i> Responsables</NavLink>
									</SidebarItem>
								}
								<SidebarItem>
									<NavLink to="/clockings/dispensers"><i className="bi bi-chevron-right"></i> Expendedores</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/clockings/dispensers-schedules"><i className="bi bi-chevron-right"></i> Control de turnos</NavLink>
								</SidebarItem>
								{ hasCapability('clockings_primary') &&
									<SidebarItem>
										<NavLink to="/clockings/stations-matching"><i className="bi bi-chevron-right"></i> Estaciones</NavLink>
									</SidebarItem>
								}
								{/* <SidebarItem>
									<NavLink to="/clockings/rules"><i className="bi bi-chevron-right"></i> Reglas</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/clockings/alerts"><i className="bi bi-chevron-right"></i> Alertas</NavLink>
								</SidebarItem> */}
							</>
						}			
					</>
				}

				{ hasCapability('users') &&
					<>
						<SidebarTitle selected={location.pathname.indexOf('users') !== -1}>
							<NavLink to="/users"><i className="bi bi-person"></i> Usuarios</NavLink>						
						</SidebarTitle>
						
						{ location.pathname.indexOf('users') !== -1 && 
							<>
								<SidebarItem>
									<NavLink to="/users"><i className="bi bi-chevron-right"></i> Listado usuarios</NavLink>
								</SidebarItem>
								<SidebarItem>
									<NavLink to="/users/add"><i className="bi bi-chevron-right"></i> Añadir usuario</NavLink>
								</SidebarItem>
							</>
						}
					</>
				}
				

				{/*<li className="nav-item">
		        	<NavLink to="/probes" className="nav-link">Sondas</NavLink>
		        </li>
		        <li className="nav-item">
		        	<NavLink to="/zones" className="nav-link">Zonas</NavLink>
		        </li>
		        <li className="nav-item">
		        	<NavLink to="/middleware-stations" className="nav-link">Estaciones</NavLink>
		        </li>
		        <li className="nav-item sign-out">
		        	<a href="/" onClick={(e) => logout(e)}>
						<FontAwesomeIcon icon={faSignOutAlt} />
					</a>
		        </li>*/}
			</Sidebar>
						
			<Content sidebarVisible={sidebarVisible} sidebarWidth={sidebarWidth}>
				{props.children}
			</Content>
		</React.Fragment>
	);
}

const Clock = (props) => {
	const [time, setTime] = useState(null);

	useEffect(() => {
		const setCurrentTime = () => {
			setTime( moment().format('HH:mm:ss') );
		}
		setCurrentTime();
		timeInterval = setInterval(() => {
			setCurrentTime();
		}, 1000);

		return function cleanup() {
			clearInterval(timeInterval);
		}
	}, []);

	return (
		<div id="clock">
			<i className="bi bi-clock"></i>
			<span>{ time }</span>
		</div>
	);
}